<template>
    <div class="relative w-full">
        <Glass blur="xs" class="w-full">
            <InputGroup :dir="$dir.value" class="h-12">
                <InputGroupInput v-model="search" class="h-full" :placeholder="$t('general.search')" />
                <InputGroupAddon align="inline-start">
                    <Transition name="fade-rotate" mode="out-in">
                        <Icon
                            :key="search ? 'close' : 'search'"
                            :name="search ? 'majesticons:close' : 'majesticons:search-line'"
                            class="block size-5! cursor-pointer"
                            :class="search ? 'text-destructive' : ''"
                            @click="handleIconClick"
                        />
                    </Transition>
                </InputGroupAddon>
                <InputGroupAddon align="inline-end">
                    <NuxtLink to="/">
                        <Logo class="max-w-16" />
                    </NuxtLink>
                </InputGroupAddon>
            </InputGroup>
        </Glass>
        <WidgetSearchResult :open :result :loading @close="handleCloseResult" />
    </div>
</template>

<script lang="ts" setup>
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/input-group";
import { Glass } from "@/components/ui/glass";
import { watchDebounced } from "@vueuse/core";

const search = ref<string>("");
const searchDebounced = ref<string>("");

// Debounce search input before triggering fetches
watchDebounced(
    search,
    (val) => {
        searchDebounced.value = val;
    },
    { debounce: 500, maxWait: 2000 },
);

const open = computed(() => Boolean(search.value?.trim().length));

const { data, pending: loading } = await useLazyAsyncData(
    `search-${searchDebounced.value}`,
    async () => {
        if (searchDebounced.value) {
            const q = encodeURIComponent(searchDebounced.value.trim());
            if (!q) return null;
            return await smartFetch<APIResponse<APISearchResult>>(`/search?query=${q}`);
        } else return null;
    },
    {
        watch: [searchDebounced],
    },
);

const result = computed(() => {
    if (!data.value?.data) return;
    return data.value?.data;
});

function handleIconClick() {
    if (search.value) {
        search.value = "";
    }
}

function handleCloseResult() {
    search.value = "";
}
</script>

<style scoped>
.fade-rotate-enter-active,
.fade-rotate-leave-active {
    transition:
        opacity 0.2s ease,
        transform 0.3s ease-out;
}
.fade-rotate-enter-from {
    opacity: 0;
    transform: rotate(-90deg);
}
.fade-rotate-leave-to {
    opacity: 0;
    transform: rotate(90deg);
}
</style>
